import { AdminCommerceActivity } from '../../components/CommerceActivity';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCoins, faMessage, faGlobe
} from '@fortawesome/free-solid-svg-icons';
import { useGetAdminBotStatsQuery } from '../../store/admin/adminApi';

const StatCard: React.FC<{
  title: string;
  value: string | number;
  icon: any;
  colorClass: string;
  iconColor?: string;
  glowClass?: string;
  subtitle?: string;
}> = ({ title, value, icon, colorClass, iconColor = 'text-white', glowClass = 'icon-box-glow-cyan', subtitle }) => (
  <div className="p-5 rounded-2xl bg-black/20 backdrop-filter backdrop-blur-xl shadow-inner-glow shadow-inner-glow-black-md drop-shadow-glow-black-md flex flex-col gap-3 relative overflow-hidden group transition-all duration-300 hover:shadow-[0_0_30px_rgba(255,255,255,0.05)] border-none">
    <div className={`absolute inset-0 bg-gradient-to-br ${colorClass} opacity-5 group-hover:opacity-20 transition-opacity duration-300 pointer-events-none`} />
    <div className="flex items-center gap-3 relative z-10">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${glowClass} group-hover:scale-110 transition-transform duration-300`}>
        <FontAwesomeIcon icon={icon} className={`text-lg ${iconColor} icon-inner-glow-white`} />
      </div>
      <div className="text-sm font-medium text-white/60">{title}</div>
    </div>
    <div className="relative z-10 flex flex-col">
      <span className="text-3xl font-bold text-white tracking-tight">
        {value}
      </span>
      {subtitle && <span className="text-xs text-white/40 mt-1">{subtitle}</span>}
    </div>
  </div>
);

const AdminBotStatsPage: React.FC = () => {
  const { t } = useTranslation();
  const { data: stats = [], isLoading } = useGetAdminBotStatsQuery();

  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="w-10 h-10 border-2 border-white/20 border-t-white/80 rounded-full animate-spin" />
      </div>
    );
  }

  if (!stats.length) {
    return (
      <div className="flex flex-col h-full w-full items-center justify-center gap-4">
        <FontAwesomeIcon icon={faGlobe} className="text-6xl text-white/20 icon-inner-glow-white" />
        <p className="text-white/60 font-medium">No global bot statistics available yet.</p>
      </div>
    );
  }

  // Aggregate global stats
  const totalTokens = stats.reduce((acc, curr) => acc + (curr.total_tokens || 0), 0);
  const totalCost = stats.reduce((acc, curr) => acc + (curr.total_cost_usd || 0), 0);
  const totalConversations = stats.reduce((acc, curr) => acc + (curr.conversations_handled || 0), 0);
  const totalActiveCommerces = new Set(stats.map(s => s.comercio_key)).size;

  return (
    <div className="flex flex-col h-full w-full p-8 gap-8 overflow-y-auto custom-scrollbar relative">
      <div className="flex flex-col gap-2 relative z-10">
        <h1 className="text-3xl font-bold text-white tracking-wide">
          {t('stats.adminBots.title', 'Global AI Agent Analytics')}
        </h1>
        <p className="text-white/60">
          {t('stats.adminBots.subtitle', 'Monitor global token consumption, costs, and conversation volume across all tenants.')}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 relative z-10">
        <StatCard
          title="Total Tokens Used"
          value={new Intl.NumberFormat().format(totalTokens)}
          icon={faCoins}
          colorClass="from-blue-500 to-cyan-500"
          iconColor="text-cyan-400"
          glowClass="icon-box-glow-cyan"
        />
        <StatCard
          title="Total Cost (USD)"
          value={`$${totalCost.toFixed(2)}`}
          icon={faCoins}
          colorClass="from-green-500 to-emerald-500"
          iconColor="text-emerald-400"
          glowClass="icon-box-glow-emerald"
        />
        <StatCard
          title="Conversations Handled"
          value={new Intl.NumberFormat().format(totalConversations)}
          icon={faMessage}
          colorClass="from-purple-500 to-pink-500"
          iconColor="text-purple-400"
          glowClass="icon-box-glow-purple"
        />
        <StatCard
          title="Active Tenants"
          value={totalActiveCommerces}
          icon={faGlobe}
          colorClass="from-yellow-500 to-orange-500"
          iconColor="text-yellow-400"
          glowClass="icon-box-glow-yellow"
        />
      </div>

      <div className="flex flex-col gap-4 relative z-10 mt-4">
        <h2 className="text-xl font-bold text-white tracking-wide">Usage by Tenant</h2>
        <div className="w-full overflow-hidden rounded-2xl bg-black/20 backdrop-filter backdrop-blur-xl shadow-inner-glow shadow-inner-glow-black-md drop-shadow-glow-black-md">
          <table className="w-full text-left text-sm text-white/80">
            <thead className="bg-white/5 text-white font-medium border-b border-white/10">
              <tr>
                <th className="px-6 py-4">Tenant (Comercio Key)</th>
                <th className="px-6 py-4">Bot ID</th>
                <th className="px-6 py-4 text-right">Tokens Used</th>
                <th className="px-6 py-4 text-right">Cost (USD)</th>
                <th className="px-6 py-4 text-right">Conversations</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {stats.map((row, idx) => (
                <tr key={`${row.comercio_key}-${row.bot_id}-${idx}`} className="hover:bg-white/5 transition-colors">
                  <td className="px-6 py-4 font-mono">{row.comercio_key}</td>
                  <td className="px-6 py-4">#{row.bot_id}</td>
                  <td className="px-6 py-4 text-right">{new Intl.NumberFormat().format(row.total_tokens || 0)}</td>
                  <td className="px-6 py-4 text-right">${(row.total_cost_usd || 0).toFixed(4)}</td>
                  <td className="px-6 py-4 text-right">{row.conversations_handled}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default function AdminStatsWithActivity() { return <div className="space-y-6"><AdminCommerceActivity /><AdminBotStatsPage /></div>; }
